import React, { useState, useEffect } from 'react';
import { buildApiUrl } from './config';
import { getPhysicalYear } from './utils/yearUtils';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, Cell } from 'recharts';

const ReportsTab = ({ students, formId }) => {
  const [activeReport, setActiveReport] = useState('mathematics');
  
  // Mathematics report states
  const [mathematicsTab, setMathematicsTab] = useState('overall');
  const [mathematicsOverallView, setMathematicsOverallView] = useState('view');
  
  // Arithmetic report states
  const [arithmeticTab, setArithmeticTab] = useState('overall');
  const [overallView, setOverallView] = useState('view');
  
  // Reading report states
  const [readingTab, setReadingTab] = useState('overall');
  const [readingOverallView, setReadingOverallView] = useState('view');
  
  // Spelling report states
  const [spellingTab, setSpellingTab] = useState('overall');
  const [spellingOverallView, setSpellingOverallView] = useState('view');
  
  // Developed Ability report states
  const [developedAbilityTab, setDevelopedAbilityTab] = useState('overall');
  const [developedAbilityOverallView, setDevelopedAbilityOverallView] = useState('view');
  
  // Mathematics data
  const [mathematicsData, setMathematicsData] = useState([]);
  const [mathScoreData, setMathScoreData] = useState([]);
  const [mathScoreRangeData, setMathScoreRangeData] = useState([]);
  const [mathEquivalentData, setMathEquivalentData] = useState([]);
  const [mathDifferenceData, setMathDifferenceData] = useState([]);
  const [numbers1EquivData, setNumbers1EquivData] = useState([]);
  const [numbers1DiffData, setNumbers1DiffData] = useState([]);
  const [numbers2EquivData, setNumbers2EquivData] = useState([]);
  const [numbers2DiffData, setNumbers2DiffData] = useState([]);
  const [measuresEquivData, setMeasuresEquivData] = useState([]);
  const [measuresDiffData, setMeasuresDiffData] = useState([]);
  const [dataHandlingEquivData, setDataHandlingEquivData] = useState([]);
  const [dataHandlingDiffData, setDataHandlingDiffData] = useState([]);
  
  // Arithmetic data
  const [arithmeticData, setArithmeticData] = useState([]);
  const [scoreData, setScoreData] = useState([]);
  const [scoreRangeData, setScoreRangeData] = useState([]);
  const [equivalentScoreData, setEquivalentScoreData] = useState([]);
  const [ageDifferenceData, setAgeDifferenceData] = useState([]);
  
  // Reading data
  const [readingData, setReadingData] = useState([]);
  const [readingScoreData, setReadingScoreData] = useState([]);
  const [readingScoreRangeData, setReadingScoreRangeData] = useState([]);
  const [readingEquivalentData, setReadingEquivalentData] = useState([]);
  const [readingDifferenceData, setReadingDifferenceData] = useState([]);
  const [wordRecognitionEquivData, setWordRecognitionEquivData] = useState([]);
  const [wordRecognitionDiffData, setWordRecognitionDiffData] = useState([]);
  const [comprehensionEquivData, setComprehensionEquivData] = useState([]);
  const [comprehensionDiffData, setComprehensionDiffData] = useState([]);
  
  // Spelling data
  const [spellingData, setSpellingData] = useState([]);
  const [spellingScoreData, setSpellingScoreData] = useState([]);
  const [spellingScoreRangeData, setSpellingScoreRangeData] = useState([]);
  const [spellingEquivalentData, setSpellingEquivalentData] = useState([]);
  const [spellingDifferenceData, setSpellingDifferenceData] = useState([]);
  
  // Developed Ability data
  const [developedAbilityData, setDevelopedAbilityData] = useState([]);
  const [developedAbilityScoreData, setDevelopedAbilityScoreData] = useState([]);
  const [developedAbilityScoreRangeData, setDevelopedAbilityScoreRangeData] = useState([]);
  const [developedAbilityEquivalentData, setDevelopedAbilityEquivalentData] = useState([]);
  const [developedAbilityDifferenceData, setDevelopedAbilityDifferenceData] = useState([]);
  const [pictureVocabEquivData, setPictureVocabEquivData] = useState([]);
  const [pictureVocabDiffData, setPictureVocabDiffData] = useState([]);
  const [nonVerbalEquivData, setNonVerbalEquivData] = useState([]);
  const [nonVerbalDiffData, setNonVerbalDiffData] = useState([]);
  
  // Operation-specific data (Arithmetic)
  const [additionEquivData, setAdditionEquivData] = useState([]);
  const [additionDiffData, setAdditionDiffData] = useState([]);
  const [subtractionEquivData, setSubtractionEquivData] = useState([]);
  const [subtractionDiffData, setSubtractionDiffData] = useState([]);
  const [multiplicationEquivData, setMultiplicationEquivData] = useState([]);
  const [multiplicationDiffData, setMultiplicationDiffData] = useState([]);
  const [divisionEquivData, setDivisionEquivData] = useState([]);
  const [divisionDiffData, setDivisionDiffData] = useState([]);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Determine year group from students (assuming first student has year_level or similar)
  const getYearGroup = () => {
    if (students.length === 0) return 1;
    const student = students[0];
    // Try common field names for year/grade
    let year = student.year_level || student.year || student.nc_year || student.form_level || 1;
    
    // If nc_year is a string like "Year 3", extract the number
    if (typeof year === 'string') {
      const match = year.match(/\d+/);
      year = match ? parseInt(match[0]) : 1;
    }
    
    // Convert to physical year for difficulty levels (handles Boys 34-41, etc.)
    year = getPhysicalYear(year);
    if (year < 1) year = 1; // Minimum year 1 for difficulty levels
    
    console.log('[ReportsTab] First student:', student);
    console.log('[ReportsTab] Detected year group:', year);
    
    return year;
  };

  const yearGroup = getYearGroup();

  // Load arithmetic data
  useEffect(() => {
    const loadArithmeticData = async () => {
      setLoading(true);
      setError('');
      try {
        // Load both years' data
        const response2025 = await fetch('/Arthematics_2025-2026.json');
        if (!response2025.ok) throw new Error('Failed to load arithmetic data 2025-2026');
        const data2025 = await response2025.json();
        
        const response2024 = await fetch('/Arthematics_2024-2025.json');
        const data2024 = response2024.ok ? await response2024.json() : [];
        
        // Merge both years' data
        const allData = [...data2025, ...data2024];
        
        // Process data for current students
        const processedData = allData
          .filter(record => students.some(s => s.full_name === record['Full Name']))
          .map(record => ({
            fullName: record['Full Name'],
            firstName: record['Full Name'].split(' ')[0],
            standardisedScore: parseFloat(record['Arithmetic (Age standardised score)']) || null,
            scoreRange: record['Arithmetic score range (age standardised score)'] || null,
            equivalentScore: parseFloat(record['Arithmetic (Age equivalent score)']) || null,
            ageDifference: parseFloat(record['Arithmetic (Age difference)']) || null,
            // Addition
            additionEquiv: parseFloat(record['Addition (Age equivalent score)']) || null,
            additionDiff: parseFloat(record['Addition (Age difference)']) || null,
            // Subtraction
            subtractionEquiv: parseFloat(record['Subtraction (Age equivalent score)']) || null,
            subtractionDiff: parseFloat(record['Subtraction (Age difference)']) || null,
            // Multiplication
            multiplicationEquiv: parseFloat(record['Multiplication (Age equivalent score)']) || null,
            multiplicationDiff: parseFloat(record['Multiplication (Age difference)']) || null,
            // Division
            divisionEquiv: parseFloat(record['Division (Age equivalent score)']) || null,
            divisionDiff: parseFloat(record['Division (Age difference)']) || null,
          }))
          .filter(record => 
            record.standardisedScore !== null || 
            record.equivalentScore !== null || 
            record.ageDifference !== null
          );

        setArithmeticData(processedData);

        // Prepare score data for chart
        const scoreChartData = processedData
          .filter(record => record.standardisedScore !== null)
          .map(record => ({
            name: record.firstName,
            score: record.standardisedScore
          }));
        setScoreData(scoreChartData);

        // Prepare score range data for chart (category distribution)
        const rangeCountMap = {};
        processedData.forEach(record => {
          if (record.scoreRange) {
            rangeCountMap[record.scoreRange] = (rangeCountMap[record.scoreRange] || 0) + 1;
          }
        });
        const scoreRangeChartData = Object.entries(rangeCountMap)
          .map(([range, count]) => ({ name: range, count }))
          .sort((a, b) => {
            const order = { 'Very low': 0, 'Low': 1, 'Low average': 2, 'High average': 3, 'High': 4, 'Very high': 5 };
            return (order[a.name] || 99) - (order[b.name] || 99);
          });
        setScoreRangeData(scoreRangeChartData);

        // Prepare equivalent score data for chart
        const equivChartData = processedData
          .filter(record => record.equivalentScore !== null)
          .map(record => ({
            name: record.firstName,
            score: record.equivalentScore
          }));
        setEquivalentScoreData(equivChartData);

        // Prepare age difference data for chart
        const diffChartData = processedData
          .filter(record => record.ageDifference !== null)
          .map(record => ({
            name: record.firstName,
            difference: record.ageDifference
          }));
        setAgeDifferenceData(diffChartData);

        // Addition data
        const addEquiv = processedData.filter(r => r.additionEquiv !== null).map(r => ({ name: r.firstName, score: r.additionEquiv }));
        const addDiff = processedData.filter(r => r.additionDiff !== null).map(r => ({ name: r.firstName, difference: r.additionDiff }));
        setAdditionEquivData(addEquiv);
        setAdditionDiffData(addDiff);

        // Subtraction data
        const subEquiv = processedData.filter(r => r.subtractionEquiv !== null).map(r => ({ name: r.firstName, score: r.subtractionEquiv }));
        const subDiff = processedData.filter(r => r.subtractionDiff !== null).map(r => ({ name: r.firstName, difference: r.subtractionDiff }));
        setSubtractionEquivData(subEquiv);
        setSubtractionDiffData(subDiff);

        // Multiplication data
        const multEquiv = processedData.filter(r => r.multiplicationEquiv !== null).map(r => ({ name: r.firstName, score: r.multiplicationEquiv }));
        const multDiff = processedData.filter(r => r.multiplicationDiff !== null).map(r => ({ name: r.firstName, difference: r.multiplicationDiff }));
        setMultiplicationEquivData(multEquiv);
        setMultiplicationDiffData(multDiff);

        // Division data
        const divEquiv = processedData.filter(r => r.divisionEquiv !== null).map(r => ({ name: r.firstName, score: r.divisionEquiv }));
        const divDiff = processedData.filter(r => r.divisionDiff !== null).map(r => ({ name: r.firstName, difference: r.divisionDiff }));
        setDivisionEquivData(divEquiv);
        setDivisionDiffData(divDiff);

      } catch (err) {
        console.error('Error loading arithmetic data:', err);
        setError('Failed to load arithmetic data. Please check the data file.');
      } finally {
        setLoading(false);
      }
    };

    if (students.length > 0) {
      loadArithmeticData();
    }
  }, [students]);

  // Load reading data
  useEffect(() => {
    const loadReadingData = async () => {
      try {
        // Load both years' data
        const response2025 = await fetch('/ReadingCEM_2025-2026.json');
        if (!response2025.ok) throw new Error('Failed to load reading data');
        const data2025 = await response2025.json();
        
        const response2024 = await fetch('/Reading_2024-2025.json');
        const data2024 = response2024.ok ? await response2024.json() : [];
        
        // Merge both years' data
        const allData = [...data2025, ...data2024];
        
        // Process data for current students
        const processedData = allData
          .filter(record => students.some(s => s.full_name === record['Full Name']))
          .map(record => ({
            fullName: record['Full Name'],
            firstName: record['Full Name'].split(' ')[0],
            standardisedScore: parseFloat(record['Reading (Age standardised score)']) || null,
            scoreRange: record['Reading score range (age standardised score)'] || null,
            equivalentScore: parseFloat(record['Reading (Age equivalent score)']) || null,
            ageDifference: parseFloat(record['Reading (Age difference)']) || null,
            wordEquiv: parseFloat(record['Word recognition (Age equivalent score)']) || null,
            wordDiff: parseFloat(record['Word recognition (Age difference)']) || null,
            comprehensionEquiv: parseFloat(record['Comprehension (Age equivalent score)']) || null,
            comprehensionDiff: parseFloat(record['Comprehension (Age difference)']) || null,
          }))
          .filter(record => 
            record.standardisedScore !== null || 
            record.equivalentScore !== null || 
            record.ageDifference !== null
          );

        setReadingData(processedData);

        // Prepare score data
        const scoreChartData = processedData
          .filter(record => record.standardisedScore !== null)
          .map(record => ({
            name: record.firstName,
            score: record.standardisedScore
          }));
        setReadingScoreData(scoreChartData);

        // Prepare score range data
        const rangeCountMap = {};
        processedData.forEach(record => {
          if (record.scoreRange) {
            rangeCountMap[record.scoreRange] = (rangeCountMap[record.scoreRange] || 0) + 1;
          }
        });
        const scoreRangeChartData = Object.entries(rangeCountMap)
          .map(([range, count]) => ({ name: range, count }))
          .sort((a, b) => {
            const order = { 'Very low': 0, 'Low': 1, 'Low average': 2, 'High average': 3, 'High': 4, 'Very high': 5 };
            return (order[a.name] || 99) - (order[b.name] || 99);
          });
        setReadingScoreRangeData(scoreRangeChartData);

        // Prepare equivalent score data
        const equivChartData = processedData
          .filter(record => record.equivalentScore !== null)
          .map(record => ({
            name: record.firstName,
            score: record.equivalentScore
          }));
        setReadingEquivalentData(equivChartData);

        // Prepare age difference data
        const diffChartData = processedData
          .filter(record => record.ageDifference !== null)
          .map(record => ({
            name: record.firstName,
            difference: record.ageDifference
          }));
        setReadingDifferenceData(diffChartData);

        // Word recognition data
        const wordEquiv = processedData.filter(r => r.wordEquiv !== null).map(r => ({ name: r.firstName, score: r.wordEquiv }));
        const wordDiff = processedData.filter(r => r.wordDiff !== null).map(r => ({ name: r.firstName, difference: r.wordDiff }));
        setWordRecognitionEquivData(wordEquiv);
        setWordRecognitionDiffData(wordDiff);

        // Comprehension data
        const compEquiv = processedData.filter(r => r.comprehensionEquiv !== null).map(r => ({ name: r.firstName, score: r.comprehensionEquiv }));
        const compDiff = processedData.filter(r => r.comprehensionDiff !== null).map(r => ({ name: r.firstName, difference: r.comprehensionDiff }));
        setComprehensionEquivData(compEquiv);
        setComprehensionDiffData(compDiff);

      } catch (err) {
        console.error('Error loading reading data:', err);
      }
    };

    if (students.length > 0) {
      loadReadingData();
    }
  }, [students]);

  // Load mathematics data
  useEffect(() => {
    const loadMathematicsData = async () => {
      try {
        // Load both years' data
        const response2025 = await fetch('/Mathematics_2025-2026.json');
        if (!response2025.ok) throw new Error('Failed to load mathematics data');
        const data2025 = await response2025.json();
        
        const response2024 = await fetch('/Mathematics_2024-2025.json');
        const data2024 = response2024.ok ? await response2024.json() : [];
        
        // Merge both years' data
        const allData = [...data2025, ...data2024];
        
        // Process data for current students
        const processedData = allData
          .filter(record => students.some(s => s.full_name === record['Full Name']))
          .map(record => ({
            fullName: record['Full Name'],
            firstName: record['Full Name'].split(' ')[0],
            standardisedScore: parseFloat(record['Mathematics (Age standardised score)']) || null,
            scoreRange: record['Mathematics score range (age standardised score)'] || null,
            equivalentScore: parseFloat(record['Mathematics (Age equivalent score)']) || null,
            ageDifference: parseFloat(record['Mathematics (Age difference)']) || null,
            numbers1Equiv: parseFloat(record['Numbers 1 (Age equivalent score)']) || null,
            numbers1Diff: parseFloat(record['Numbers 1 (Age difference)']) || null,
            numbers2Equiv: parseFloat(record['Numbers 2 (Age equivalent score)']) || null,
            numbers2Diff: parseFloat(record['Numbers 2 (Age difference)']) || null,
            measuresEquiv: parseFloat(record['Measures, shape and space (Age equivalent score)']) || null,
            measuresDiff: parseFloat(record['Measures, shape and space (Age difference)']) || null,
            dataHandlingEquiv: parseFloat(record['Data handling (Age equivalent score)']) || null,
            dataHandlingDiff: parseFloat(record['Data handling (Age difference)']) || null,
          }))
          .filter(record => 
            record.standardisedScore !== null || 
            record.equivalentScore !== null || 
            record.ageDifference !== null
          );

        setMathematicsData(processedData);

        // Prepare score data
        const scoreChartData = processedData
          .filter(record => record.standardisedScore !== null)
          .map(record => ({
            name: record.firstName,
            score: record.standardisedScore
          }));
        setMathScoreData(scoreChartData);

        // Prepare score range data
        const rangeCountMap = {};
        processedData.forEach(record => {
          if (record.scoreRange) {
            rangeCountMap[record.scoreRange] = (rangeCountMap[record.scoreRange] || 0) + 1;
          }
        });
        const scoreRangeChartData = Object.entries(rangeCountMap)
          .map(([range, count]) => ({ name: range, count }))
          .sort((a, b) => {
            const order = { 'Very low': 0, 'Low': 1, 'Low average': 2, 'High average': 3, 'High': 4, 'Very high': 5 };
            return (order[a.name] || 99) - (order[b.name] || 99);
          });
        setMathScoreRangeData(scoreRangeChartData);

        // Prepare equivalent score data
        const equivChartData = processedData
          .filter(record => record.equivalentScore !== null)
          .map(record => ({
            name: record.firstName,
            score: record.equivalentScore
          }));
        setMathEquivalentData(equivChartData);

        // Prepare age difference data
        const diffChartData = processedData
          .filter(record => record.ageDifference !== null)
          .map(record => ({
            name: record.firstName,
            difference: record.ageDifference
          }));
        setMathDifferenceData(diffChartData);

        // Numbers 1 data
        const num1Equiv = processedData.filter(r => r.numbers1Equiv !== null).map(r => ({ name: r.firstName, score: r.numbers1Equiv }));
        const num1Diff = processedData.filter(r => r.numbers1Diff !== null).map(r => ({ name: r.firstName, difference: r.numbers1Diff }));
        setNumbers1EquivData(num1Equiv);
        setNumbers1DiffData(num1Diff);

        // Numbers 2 data
        const num2Equiv = processedData.filter(r => r.numbers2Equiv !== null).map(r => ({ name: r.firstName, score: r.numbers2Equiv }));
        const num2Diff = processedData.filter(r => r.numbers2Diff !== null).map(r => ({ name: r.firstName, difference: r.numbers2Diff }));
        setNumbers2EquivData(num2Equiv);
        setNumbers2DiffData(num2Diff);

        // Measures data
        const measEquiv = processedData.filter(r => r.measuresEquiv !== null).map(r => ({ name: r.firstName, score: r.measuresEquiv }));
        const measDiff = processedData.filter(r => r.measuresDiff !== null).map(r => ({ name: r.firstName, difference: r.measuresDiff }));
        setMeasuresEquivData(measEquiv);
        setMeasuresDiffData(measDiff);

        // Data handling data
        const dataEquiv = processedData.filter(r => r.dataHandlingEquiv !== null).map(r => ({ name: r.firstName, score: r.dataHandlingEquiv }));
        const dataDiff = processedData.filter(r => r.dataHandlingDiff !== null).map(r => ({ name: r.firstName, difference: r.dataHandlingDiff }));
        setDataHandlingEquivData(dataEquiv);
        setDataHandlingDiffData(dataDiff);

      } catch (err) {
        console.error('Error loading mathematics data:', err);
      }
    };

    if (students.length > 0) {
      loadMathematicsData();
    }
  }, [students]);

  // Load Spelling Assessment Data
  useEffect(() => {
    setLoading(true);
    const loadSpellingData = async () => {
      try {
        // Load both years' data
        console.log('Loading Spelling_2025-2026.json...');
        const response2025 = await fetch('/Spelling_2025-2026.json');
        console.log('Response 2025 status:', response2025.status, response2025.statusText);
        
        if (!response2025.ok) {
          console.error('Response status:', response2025.status, response2025.statusText);
          throw new Error(`Failed to load spelling 2025-2026 data: ${response2025.status} ${response2025.statusText}`);
        }
        
        const text2025 = await response2025.text();
        console.log('Response text first 200 chars:', text2025.substring(0, 200));
        
        let data2025;
        try {
          data2025 = JSON.parse(text2025);
          console.log('Successfully parsed 2025-2026 data, records:', data2025.length);
        } catch (parseErr) {
          console.error('JSON parse error for 2025-2026 data:', parseErr, 'Response text:', text2025.substring(0, 500));
          throw parseErr;
        }
        
        const response2024 = await fetch('/Spelling_2024-2025.json');
        let data2024 = [];
        if (response2024.ok) {
          const text2024 = await response2024.text();
          try {
            data2024 = JSON.parse(text2024);
            console.log('Successfully parsed 2024-2025 data, records:', data2024.length);
          } catch (parseErr) {
            console.error('JSON parse error for 2024-2025 data:', parseErr);
            data2024 = [];
          }
        }
        
        // Merge both years' data
        const allData = [...data2025, ...data2024];
        console.log('Total merged records:', allData.length);
        setSpellingData(allData);

        const processedData = allData
          .filter(record => students.some(s => s.full_name === record['Full Name']))
          .map(record => ({
            firstName: record['Full Name']?.split(' ')[0] || 'Unknown',
            fullName: record['Full Name'],
            score: record['Spelling (Age standardised score)'] !== null ? record['Spelling (Age standardised score)'] : null,
            range: record['Spelling score range (age standardised score)'] || 'Unknown',
            equivalent: record['Spelling (Age equivalent score)'] !== null ? record['Spelling (Age equivalent score)'] : null,
            difference: record['Spelling (Age difference)'] !== null ? record['Spelling (Age difference)'] : null,
          }));

        // Process score data
        const scoreRecords = processedData.filter(r => r.score !== null).map(r => ({ name: r.firstName, score: r.score }));
        setSpellingScoreData(scoreRecords);
        
        // Process score range distribution
        const rangeCounts = {};
        processedData.forEach(r => {
          rangeCounts[r.range] = (rangeCounts[r.range] || 0) + 1;
        });
        const scoreRangeRecords = Object.entries(rangeCounts).map(([range, count]) => ({ name: range, count }));
        setSpellingScoreRangeData(scoreRangeRecords);

        // Process equivalent score data
        const equivRecords = processedData.filter(r => r.equivalent !== null).map(r => ({ name: r.firstName, score: r.equivalent }));
        setSpellingEquivalentData(equivRecords);

        // Process difference data
        const diffRecords = processedData.filter(r => r.difference !== null).map(r => ({ name: r.firstName, difference: r.difference }));
        setSpellingDifferenceData(diffRecords);

        setError(null);
        setLoading(false);
      } catch (err) {
        console.error('Error loading spelling data:', err);
        setError('Failed to load spelling data');
        setLoading(false);
      }
    };

    if (students.length > 0) {
      loadSpellingData();
    }
  }, [students]);

  // Load Developed Ability Assessment Data
  useEffect(() => {
    setLoading(true);
    const loadDevelopedAbilityData = async () => {
      try {
        // Load both years' data
        const response2025 = await fetch('/Developed_ability_2025-2026.json');
        const data2025 = await response2025.json();
        
        const response2024 = await fetch('/Developed_ability_2024-2025.json');
        const data2024 = response2024.ok ? await response2024.json() : [];
        
        // Merge both years' data
        const allData = [...data2025, ...data2024];
        setDevelopedAbilityData(allData);

        const processedData = allData
          .filter(record => students.some(s => s.full_name === record['Full Name']))
          .map(record => ({
            firstName: record['Full Name']?.split(' ')[0] || 'Unknown',
            fullName: record['Full Name'],
            score: record['Developed ability (Age standardised score)'] !== null && record['Developed ability (Age standardised score)'] !== '<55' ? record['Developed ability (Age standardised score)'] : null,
            range: record['Developed ability score range (age standardised score)'] || 'Unknown',
            equivalent: record['Developed ability (Age equivalent score)'] !== null ? record['Developed ability (Age equivalent score)'] : null,
            difference: record['Developed ability (Age difference)'] !== null ? record['Developed ability (Age difference)'] : null,
            pictureVocabEquiv: record['Picture vocabulary (Age equivalent score)'] !== null ? record['Picture vocabulary (Age equivalent score)'] : null,
            pictureVocabDiff: record['Picture vocabulary (Age difference)'] !== null ? record['Picture vocabulary (Age difference)'] : null,
            nonVerbalEquiv: record['Non-verbal (Age equivalent score)'] !== null ? record['Non-verbal (Age equivalent score)'] : null,
            nonVerbalDiff: record['Non-verbal (Age difference)'] !== null ? record['Non-verbal (Age difference)'] : null,
          }));

        // Process overall score data
        const scoreRecords = processedData.filter(r => r.score !== null).map(r => ({ name: r.firstName, score: r.score }));
        setDevelopedAbilityScoreData(scoreRecords);
        
        // Process score range distribution
        const rangeCounts = {};
        processedData.forEach(r => {
          rangeCounts[r.range] = (rangeCounts[r.range] || 0) + 1;
        });
        const scoreRangeRecords = Object.entries(rangeCounts).map(([range, count]) => ({ name: range, count }));
        setDevelopedAbilityScoreRangeData(scoreRangeRecords);

        // Process equivalent score data
        const equivRecords = processedData.filter(r => r.equivalent !== null).map(r => ({ name: r.firstName, score: r.equivalent }));
        setDevelopedAbilityEquivalentData(equivRecords);

        // Process difference data
        const diffRecords = processedData.filter(r => r.difference !== null).map(r => ({ name: r.firstName, difference: r.difference }));
        setDevelopedAbilityDifferenceData(diffRecords);

        // Process Picture Vocabulary data
        const pictureVocabEquiv = processedData.filter(r => r.pictureVocabEquiv !== null).map(r => ({ name: r.firstName, score: r.pictureVocabEquiv }));
        setPictureVocabEquivData(pictureVocabEquiv);
        const pictureVocabDiff = processedData.filter(r => r.pictureVocabDiff !== null).map(r => ({ name: r.firstName, difference: r.pictureVocabDiff }));
        setPictureVocabDiffData(pictureVocabDiff);

        // Process Non-verbal data
        const nonVerbalEquiv = processedData.filter(r => r.nonVerbalEquiv !== null).map(r => ({ name: r.firstName, score: r.nonVerbalEquiv }));
        setNonVerbalEquivData(nonVerbalEquiv);
        const nonVerbalDiff = processedData.filter(r => r.nonVerbalDiff !== null).map(r => ({ name: r.firstName, difference: r.nonVerbalDiff }));
        setNonVerbalDiffData(nonVerbalDiff);

        setError(null);
      } catch (err) {
        console.error('Error loading developed ability data:', err);
      }
    };

    if (students.length > 0) {
      loadDevelopedAbilityData();
    }
  }, [students]);

  // Function to get color based on score
  const getScoreColor = (score) => {
    if (score < 85) return '#ef4444'; // red
    if (score < 93) return '#fca5a5'; // light red
    if (score < 108) return '#22c55e'; // green
    if (score < 120) return '#93c5fd'; // light blue
    if (score < 130) return '#3b82f6'; // blue
    return '#fb923c'; // orange
  };

  // Color mapping for score ranges
  const getScoreRangeColor = (range) => {
    const colors = {
      'Very low': '#dc2626',
      'Low': '#ef4444',
      'Low average': '#fca5a5',
      'High average': '#22c55e',
      'High': '#3b82f6',
      'Very high': '#fb923c'
    };
    return colors[range] || '#9ca3af';
  };

  const OverallView = () => (
    <div className="space-y-6">
      {/* Sub-tabs for Overall view */}
      <div className="flex gap-2 border-b border-gray-200 mb-6">
        <button
          onClick={() => setOverallView('view')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-all ${
            overallView === 'view'
              ? 'bg-blue-100 text-blue-800 border-b-2 border-blue-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          📊 View
        </button>
        <button
          onClick={() => setOverallView('score')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-all ${
            overallView === 'score'
              ? 'bg-blue-100 text-blue-800 border-b-2 border-blue-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          📈 Score
        </button>
        <button
          onClick={() => setOverallView('age')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-all ${
            overallView === 'age'
              ? 'bg-blue-100 text-blue-800 border-b-2 border-blue-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          🎂 Age
        </button>
      </div>

      {/* Content based on selected view */}
      {overallView === 'view' ? (
        <div className="space-y-8">
          {/* Row 1: Score Charts */}
          <div className="grid grid-cols-2 gap-8">
            {/* Score Chart */}
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">Arithmetic (Age Standardised Score)</h3>
                <p className="text-sm text-blue-700">Average: {
                  scoreData.length > 0 
                    ? (scoreData.reduce((sum, d) => sum + d.score, 0) / scoreData.length).toFixed(1)
                    : 'N/A'
                }</p>
              </div>
              {scoreData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={[...scoreData].sort((a, b) => a.score - b.score)} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                    <YAxis label={{ value: 'Score', angle: -90, position: 'insideLeft' }} />
                    <Tooltip />
                    <Bar dataKey="score" radius={[8, 8, 0, 0]}>
                      {[...scoreData].sort((a, b) => a.score - b.score).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getScoreColor(entry.score)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-gray-500">No score data available</div>
              )}
            </div>

            {/* Score Range Chart */}
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h3 className="text-lg font-semibold text-purple-900 mb-2">Score Range Distribution</h3>
                <p className="text-sm text-purple-700">Class distribution by performance level</p>
              </div>
              {scoreRangeData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={scoreRangeData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                    <YAxis label={{ value: 'Count', angle: -90, position: 'insideLeft' }} />
                    <Tooltip />
                    <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                      {scoreRangeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getScoreRangeColor(entry.name)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-gray-500">No range data available</div>
              )}
            </div>
          </div>

          {/* Row 2: Age Charts */}
          <div className="grid grid-cols-2 gap-8">
            {/* Equivalent Score Chart */}
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="text-lg font-semibold text-green-900 mb-2">Arithmetic (Age Equivalent Score)</h3>
                <p className="text-sm text-green-700">Average: {
                  equivalentScoreData.length > 0 
                    ? (equivalentScoreData.reduce((sum, d) => sum + d.score, 0) / equivalentScoreData.length).toFixed(1)
                    : 'N/A'
                }</p>
              </div>
              {equivalentScoreData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={[...equivalentScoreData].sort((a, b) => a.score - b.score)} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                    <YAxis label={{ value: 'Score', angle: -90, position: 'insideLeft' }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="score" stroke="#10b981" dot={{ fill: '#10b981' }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-gray-500">No data available</div>
              )}
            </div>

            {/* Age Difference Chart */}
            <div className="space-y-4">
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h3 className="text-lg font-semibold text-orange-900 mb-2">Arithmetic (Age Difference)</h3>
                <p className="text-sm text-orange-700">Average difference: {
                  ageDifferenceData.length > 0 
                    ? (ageDifferenceData.reduce((sum, d) => sum + d.difference, 0) / ageDifferenceData.length).toFixed(2)
                    : 'N/A'
                } years</p>
              </div>
              {ageDifferenceData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={[...ageDifferenceData].sort((a, b) => a.difference - b.difference)} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                    <YAxis 
                      tickFormatter={(value) => Math.round(value)}
                      label={{ value: 'Difference (years)', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip />
                    <Line type="monotone" dataKey="difference" stroke="#f97316" dot={{ fill: '#f97316' }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-gray-500">No data available</div>
              )}
            </div>
          </div>
        </div>
      ) : overallView === 'score' ? (
        <ScoreView />
      ) : (
        <AgeView />
      )}
    </div>
  );

  const ScoreView = () => {
    // Sort data from smallest to biggest score
    const sortedScoreData = [...scoreData].sort((a, b) => a.score - b.score);
    
    return (
    <div className="space-y-8">
      {/* Standardised Score Chart */}
      <div className="space-y-6">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">Arithmetic (Age Standardised Score)</h3>
          <p className="text-sm text-blue-700">Average score across class: {
            scoreData.length > 0 
              ? (scoreData.reduce((sum, d) => sum + d.score, 0) / scoreData.length).toFixed(1)
              : 'N/A'
          }</p>
        </div>

        {scoreData.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={sortedScoreData} margin={{ top: 20, right: 30, left: 20, bottom: 120 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                angle={-45} 
                textAnchor="end" 
                height={100}
              />
              <YAxis label={{ value: 'Score', angle: -90, position: 'insideLeft' }} />
              <Tooltip />
              <Bar dataKey="score" radius={[8, 8, 0, 0]}>
                {sortedScoreData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getScoreColor(entry.score)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-12 text-gray-500">
            No standardised score data available
          </div>
        )}
      </div>

      {/* Score Range Distribution Chart */}
      <div className="space-y-6">
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <h3 className="text-lg font-semibold text-purple-900 mb-2">Score Range Distribution</h3>
          <p className="text-sm text-purple-700">Class distribution by performance level</p>
        </div>

        {scoreRangeData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={scoreRangeData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
              <YAxis label={{ value: 'Count', angle: -90, position: 'insideLeft' }} />
              <Tooltip />
              <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                {scoreRangeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getScoreRangeColor(entry.name)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-12 text-gray-500">
            No score range data available
          </div>
        )}
      </div>
    </div>
    );
  };

  const AgeView = () => {
    // Sort equivalent score data (smallest to highest) - keep decimal values
    const sortedEquivData = [...equivalentScoreData]
      .sort((a, b) => a.score - b.score);

    // Sort age difference data (smallest to highest) - keep decimal values
    const sortedDiffData = [...ageDifferenceData]
      .sort((a, b) => a.difference - b.difference);

    // Calculate Y-axis domain for equivalent score (lowest - 1 to highest)
    const scoreValues = sortedEquivData.map(d => d.score).filter(v => v !== null);
    const scoreMin = scoreValues.length > 0 ? Math.min(...scoreValues) - 1 : 0;
    const scoreMax = scoreValues.length > 0 ? Math.max(...scoreValues) : 10;

    // Calculate Y-axis domain for age difference (lowest - 1 to highest)
    const diffValues = sortedDiffData.map(d => d.difference).filter(v => v !== null);
    const diffMin = diffValues.length > 0 ? Math.min(...diffValues) - 1 : -5;
    const diffMax = diffValues.length > 0 ? Math.max(...diffValues) : 5;

    return (
    <div className="space-y-8">
      <div className="space-y-4">
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <h3 className="text-lg font-semibold text-green-900 mb-2">Arithmetic (Age Equivalent Score)</h3>
          <p className="text-sm text-green-700">Average: {
            equivalentScoreData.length > 0 
              ? (equivalentScoreData.reduce((sum, d) => sum + d.score, 0) / equivalentScoreData.length).toFixed(1)
              : 'N/A'
          }</p>
        </div>

        {equivalentScoreData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={sortedEquivData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                angle={-45} 
                textAnchor="end" 
                height={80}
              />
              <YAxis 
                domain={[scoreMin, scoreMax]}
                tickFormatter={(value) => Math.round(value)}
                label={{ value: 'Score', angle: -90, position: 'insideLeft' }} 
              />
              <Tooltip />
              <Line type="monotone" dataKey="score" stroke="#10b981" dot={{ fill: '#10b981' }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No equivalent score data available
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <h3 className="text-lg font-semibold text-purple-900 mb-2">Arithmetic (Age Difference)</h3>
          <p className="text-sm text-purple-700">Average difference: {
            ageDifferenceData.length > 0 
              ? (ageDifferenceData.reduce((sum, d) => sum + d.difference, 0) / ageDifferenceData.length).toFixed(2)
              : 'N/A'
          } years</p>
        </div>

        {ageDifferenceData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={sortedDiffData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                angle={-45} 
                textAnchor="end" 
                height={80}
              />
              <YAxis 
                domain={[diffMin, diffMax]}
                tickFormatter={(value) => Math.round(value)}
                label={{ value: 'Difference (years)', angle: -90, position: 'insideLeft' }} 
              />
              <Tooltip />
              <Line type="monotone" dataKey="difference" stroke="#a855f7" dot={{ fill: '#a855f7' }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No age difference data available
          </div>
        )}
      </div>
    </div>
    );
  };

  // Helper function to create operation views (Addition, Subtraction, etc.)
  const OperationView = ({ title, icon, equivData, diffData }) => {
    const sortedEquivData = [...equivData].sort((a, b) => a.score - b.score);
    const sortedDiffData = [...diffData].sort((a, b) => a.difference - b.difference);

    const scoreValues = sortedEquivData.map(d => d.score).filter(v => v !== null);
    const scoreMin = scoreValues.length > 0 ? Math.min(...scoreValues) - 1 : 0;
    const scoreMax = scoreValues.length > 0 ? Math.max(...scoreValues) : 10;

    const diffValues = sortedDiffData.map(d => d.difference).filter(v => v !== null);
    const diffMin = diffValues.length > 0 ? Math.min(...diffValues) - 1 : -5;
    const diffMax = diffValues.length > 0 ? Math.max(...diffValues) : 5;

    return (
      <div className="space-y-8">
        <div className="grid grid-cols-1 gap-8">
          {/* Equivalent Score Chart */}
          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="text-lg font-semibold text-green-900 mb-2">{icon} {title} (Age Equivalent Score)</h3>
              <p className="text-sm text-green-700">Average: {
                equivData.length > 0 
                  ? (equivData.reduce((sum, d) => sum + d.score, 0) / equivData.length).toFixed(1)
                  : 'N/A'
              }</p>
            </div>
            {equivData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={sortedEquivData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                  <YAxis 
                    domain={[scoreMin, scoreMax]}
                    tickFormatter={(value) => Math.round(value)}
                    label={{ value: 'Score', angle: -90, position: 'insideLeft' }} 
                  />
                  <Tooltip />
                  <Line type="monotone" dataKey="score" stroke="#10b981" dot={{ fill: '#10b981' }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-8 text-gray-500">No data available</div>
            )}
          </div>

          {/* Age Difference Chart */}
          <div className="space-y-4">
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <h3 className="text-lg font-semibold text-orange-900 mb-2">{icon} {title} (Age Difference)</h3>
              <p className="text-sm text-orange-700">Average difference: {
                diffData.length > 0 
                  ? (diffData.reduce((sum, d) => sum + d.difference, 0) / diffData.length).toFixed(2)
                  : 'N/A'
              } years</p>
            </div>
            {diffData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={sortedDiffData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                  <YAxis 
                    domain={[diffMin, diffMax]}
                    tickFormatter={(value) => Math.round(value)}
                    label={{ value: 'Difference (years)', angle: -90, position: 'insideLeft' }} 
                  />
                  <Tooltip />
                  <Line type="monotone" dataKey="difference" stroke="#f97316" dot={{ fill: '#f97316' }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-8 text-gray-500">No data available</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Mathematics Report Components
  const MathematicsOverallView = () => (
    <div className="space-y-6">
      {/* Sub-tabs for Mathematics Overall view */}
      <div className="flex gap-2 border-b border-gray-200 mb-6">
        <button
          onClick={() => setMathematicsOverallView('view')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-all ${
            mathematicsOverallView === 'view'
              ? 'bg-blue-100 text-blue-800 border-b-2 border-blue-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          📊 View
        </button>
        <button
          onClick={() => setMathematicsOverallView('score')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-all ${
            mathematicsOverallView === 'score'
              ? 'bg-blue-100 text-blue-800 border-b-2 border-blue-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          📈 Score
        </button>
        <button
          onClick={() => setMathematicsOverallView('age')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-all ${
            mathematicsOverallView === 'age'
              ? 'bg-blue-100 text-blue-800 border-b-2 border-blue-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          🎂 Age
        </button>
      </div>

      {/* Content based on selected view */}
      {mathematicsOverallView === 'view' ? (
        <div className="space-y-8">
          <div className="grid grid-cols-2 gap-8">
            {/* Score Chart */}
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">Mathematics (Age Standardised Score)</h3>
                <p className="text-sm text-blue-700">Average: {
                  mathScoreData.length > 0 
                    ? (mathScoreData.reduce((sum, d) => sum + d.score, 0) / mathScoreData.length).toFixed(1)
                    : 'N/A'
                }</p>
              </div>
              {mathScoreData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={[...mathScoreData].sort((a, b) => a.score - b.score)} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                    <YAxis label={{ value: 'Score', angle: -90, position: 'insideLeft' }} />
                    <Tooltip />
                    <Bar dataKey="score" radius={[8, 8, 0, 0]}>
                      {[...mathScoreData].sort((a, b) => a.score - b.score).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getScoreColor(entry.score)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-gray-500">No score data available</div>
              )}
            </div>

            {/* Score Range Chart */}
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h3 className="text-lg font-semibold text-purple-900 mb-2">Score Range Distribution</h3>
                <p className="text-sm text-purple-700">Class distribution by performance level</p>
              </div>
              {mathScoreRangeData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={mathScoreRangeData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                    <YAxis label={{ value: 'Count', angle: -90, position: 'insideLeft' }} />
                    <Tooltip />
                    <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                      {mathScoreRangeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getScoreRangeColor(entry.name)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-gray-500">No range data available</div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8">
            {/* Equivalent Score Chart */}
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="text-lg font-semibold text-green-900 mb-2">Mathematics (Age Equivalent Score)</h3>
                <p className="text-sm text-green-700">Average: {
                  mathEquivalentData.length > 0 
                    ? (mathEquivalentData.reduce((sum, d) => sum + d.score, 0) / mathEquivalentData.length).toFixed(1)
                    : 'N/A'
                }</p>
              </div>
              {mathEquivalentData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={[...mathEquivalentData].sort((a, b) => a.score - b.score)} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                    <YAxis label={{ value: 'Score', angle: -90, position: 'insideLeft' }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="score" stroke="#10b981" dot={{ fill: '#10b981' }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-gray-500">No data available</div>
              )}
            </div>

            {/* Age Difference Chart */}
            <div className="space-y-4">
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h3 className="text-lg font-semibold text-orange-900 mb-2">Mathematics (Age Difference)</h3>
                <p className="text-sm text-orange-700">Average difference: {
                  mathDifferenceData.length > 0 
                    ? (mathDifferenceData.reduce((sum, d) => sum + d.difference, 0) / mathDifferenceData.length).toFixed(2)
                    : 'N/A'
                } years</p>
              </div>
              {mathDifferenceData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={[...mathDifferenceData].sort((a, b) => a.difference - b.difference)} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                    <YAxis 
                      tickFormatter={(value) => Math.round(value)}
                      label={{ value: 'Difference (years)', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip />
                    <Line type="monotone" dataKey="difference" stroke="#f97316" dot={{ fill: '#f97316' }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-gray-500">No data available</div>
              )}
            </div>
          </div>
        </div>
      ) : mathematicsOverallView === 'score' ? (
        <MathematicsScoreView />
      ) : (
        <MathematicsAgeView />
      )}
    </div>
  );

  const MathematicsScoreView = () => {
    const sortedScoreData = [...mathScoreData].sort((a, b) => a.score - b.score);
    
    return (
    <div className="space-y-8">
      <div className="space-y-6">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">Mathematics (Age Standardised Score)</h3>
          <p className="text-sm text-blue-700">Average score across class: {
            mathScoreData.length > 0 
              ? (mathScoreData.reduce((sum, d) => sum + d.score, 0) / mathScoreData.length).toFixed(1)
              : 'N/A'
          }</p>
        </div>

        {mathScoreData.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={sortedScoreData} margin={{ top: 20, right: 30, left: 20, bottom: 120 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                angle={-45} 
                textAnchor="end" 
                height={100}
              />
              <YAxis label={{ value: 'Score', angle: -90, position: 'insideLeft' }} />
              <Tooltip />
              <Bar dataKey="score" radius={[8, 8, 0, 0]}>
                {sortedScoreData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getScoreColor(entry.score)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-12 text-gray-500">
            No standardised score data available
          </div>
        )}
      </div>

      <div className="space-y-6">
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <h3 className="text-lg font-semibold text-purple-900 mb-2">Score Range Distribution</h3>
          <p className="text-sm text-purple-700">Class distribution by performance level</p>
        </div>

        {mathScoreRangeData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={mathScoreRangeData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
              <YAxis label={{ value: 'Count', angle: -90, position: 'insideLeft' }} />
              <Tooltip />
              <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                {mathScoreRangeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getScoreRangeColor(entry.name)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-12 text-gray-500">
            No score range data available
          </div>
        )}
      </div>
    </div>
    );
  };

  const MathematicsAgeView = () => {
    const sortedEquivData = [...mathEquivalentData].sort((a, b) => a.score - b.score);
    const sortedDiffData = [...mathDifferenceData].sort((a, b) => a.difference - b.difference);

    const scoreValues = sortedEquivData.map(d => d.score).filter(v => v !== null);
    const scoreMin = scoreValues.length > 0 ? Math.min(...scoreValues) - 1 : 0;
    const scoreMax = scoreValues.length > 0 ? Math.max(...scoreValues) : 10;

    const diffValues = sortedDiffData.map(d => d.difference).filter(v => v !== null);
    const diffMin = diffValues.length > 0 ? Math.min(...diffValues) - 1 : -5;
    const diffMax = diffValues.length > 0 ? Math.max(...diffValues) : 5;

    return (
    <div className="space-y-8">
      <div className="space-y-4">
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <h3 className="text-lg font-semibold text-green-900 mb-2">Mathematics (Age Equivalent Score)</h3>
          <p className="text-sm text-green-700">Average: {
            mathEquivalentData.length > 0 
              ? (mathEquivalentData.reduce((sum, d) => sum + d.score, 0) / mathEquivalentData.length).toFixed(1)
              : 'N/A'
          }</p>
        </div>

        {mathEquivalentData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={sortedEquivData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                angle={-45} 
                textAnchor="end" 
                height={80}
              />
              <YAxis 
                domain={[scoreMin, scoreMax]}
                tickFormatter={(value) => Math.round(value)}
                label={{ value: 'Score', angle: -90, position: 'insideLeft' }} 
              />
              <Tooltip />
              <Line type="monotone" dataKey="score" stroke="#10b981" dot={{ fill: '#10b981' }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No equivalent score data available
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <h3 className="text-lg font-semibold text-purple-900 mb-2">Mathematics (Age Difference)</h3>
          <p className="text-sm text-purple-700">Average difference: {
            mathDifferenceData.length > 0 
              ? (mathDifferenceData.reduce((sum, d) => sum + d.difference, 0) / mathDifferenceData.length).toFixed(2)
              : 'N/A'
          } years</p>
        </div>

        {mathDifferenceData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={sortedDiffData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                angle={-45} 
                textAnchor="end" 
                height={80}
              />
              <YAxis 
                domain={[diffMin, diffMax]}
                tickFormatter={(value) => Math.round(value)}
                label={{ value: 'Difference (years)', angle: -90, position: 'insideLeft' }} 
              />
              <Tooltip />
              <Line type="monotone" dataKey="difference" stroke="#a855f7" dot={{ fill: '#a855f7' }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No age difference data available
          </div>
        )}
      </div>
    </div>
    );
  };

  const MathematicsSubjectsView = ({ title, icon, equivData, diffData }) => {
    const sortedEquivData = [...equivData].sort((a, b) => a.score - b.score);
    const sortedDiffData = [...diffData].sort((a, b) => a.difference - b.difference);

    const scoreValues = sortedEquivData.map(d => d.score).filter(v => v !== null);
    const scoreMin = scoreValues.length > 0 ? Math.min(...scoreValues) - 1 : 0;
    const scoreMax = scoreValues.length > 0 ? Math.max(...scoreValues) : 10;

    const diffValues = sortedDiffData.map(d => d.difference).filter(v => v !== null);
    const diffMin = diffValues.length > 0 ? Math.min(...diffValues) - 1 : -5;
    const diffMax = diffValues.length > 0 ? Math.max(...diffValues) : 5;

    return (
      <div className="space-y-8">
        <div className="grid grid-cols-1 gap-8">
          {/* Equivalent Score Chart */}
          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="text-lg font-semibold text-green-900 mb-2">{icon} {title} (Age Equivalent Score)</h3>
              <p className="text-sm text-green-700">Average: {
                equivData.length > 0 
                  ? (equivData.reduce((sum, d) => sum + d.score, 0) / equivData.length).toFixed(1)
                  : 'N/A'
              }</p>
            </div>
            {equivData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={sortedEquivData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                  <YAxis 
                    domain={[scoreMin, scoreMax]}
                    tickFormatter={(value) => Math.round(value)}
                    label={{ value: 'Score', angle: -90, position: 'insideLeft' }} 
                  />
                  <Tooltip />
                  <Line type="monotone" dataKey="score" stroke="#10b981" dot={{ fill: '#10b981' }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-8 text-gray-500">No data available</div>
            )}
          </div>

          {/* Age Difference Chart */}
          <div className="space-y-4">
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <h3 className="text-lg font-semibold text-orange-900 mb-2">{icon} {title} (Age Difference)</h3>
              <p className="text-sm text-orange-700">Average difference: {
                diffData.length > 0 
                  ? (diffData.reduce((sum, d) => sum + d.difference, 0) / diffData.length).toFixed(2)
                  : 'N/A'
              } years</p>
            </div>
            {diffData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={sortedDiffData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                  <YAxis 
                    domain={[diffMin, diffMax]}
                    tickFormatter={(value) => Math.round(value)}
                    label={{ value: 'Difference (years)', angle: -90, position: 'insideLeft' }} 
                  />
                  <Tooltip />
                  <Line type="monotone" dataKey="difference" stroke="#f97316" dot={{ fill: '#f97316' }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-8 text-gray-500">No data available</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Reading Report Components
  const ReadingOverallView = () => (
    <div className="space-y-6">
      {/* Sub-tabs for Reading Overall view */}
      <div className="flex gap-2 border-b border-gray-200 mb-6">
        <button
          onClick={() => setReadingOverallView('view')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-all ${
            readingOverallView === 'view'
              ? 'bg-blue-100 text-blue-800 border-b-2 border-blue-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          📊 View
        </button>
        <button
          onClick={() => setReadingOverallView('score')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-all ${
            readingOverallView === 'score'
              ? 'bg-blue-100 text-blue-800 border-b-2 border-blue-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          📈 Score
        </button>
        <button
          onClick={() => setReadingOverallView('age')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-all ${
            readingOverallView === 'age'
              ? 'bg-blue-100 text-blue-800 border-b-2 border-blue-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          🎂 Age
        </button>
      </div>

      {/* Content based on selected view */}
      {readingOverallView === 'view' ? (
        <div className="space-y-8">
          <div className="grid grid-cols-2 gap-8">
            {/* Score Chart */}
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">Reading (Age Standardised Score)</h3>
                <p className="text-sm text-blue-700">Average: {
                  readingScoreData.length > 0 
                    ? (readingScoreData.reduce((sum, d) => sum + d.score, 0) / readingScoreData.length).toFixed(1)
                    : 'N/A'
                }</p>
              </div>
              {readingScoreData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={[...readingScoreData].sort((a, b) => a.score - b.score)} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                    <YAxis label={{ value: 'Score', angle: -90, position: 'insideLeft' }} />
                    <Tooltip />
                    <Bar dataKey="score" radius={[8, 8, 0, 0]}>
                      {[...readingScoreData].sort((a, b) => a.score - b.score).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getScoreColor(entry.score)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-gray-500">No score data available</div>
              )}
            </div>

            {/* Score Range Chart */}
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h3 className="text-lg font-semibold text-purple-900 mb-2">Score Range Distribution</h3>
                <p className="text-sm text-purple-700">Class distribution by performance level</p>
              </div>
              {readingScoreRangeData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={readingScoreRangeData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                    <YAxis label={{ value: 'Count', angle: -90, position: 'insideLeft' }} />
                    <Tooltip />
                    <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                      {readingScoreRangeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getScoreRangeColor(entry.name)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-gray-500">No range data available</div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8">
            {/* Equivalent Score Chart */}
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="text-lg font-semibold text-green-900 mb-2">Reading (Age Equivalent Score)</h3>
                <p className="text-sm text-green-700">Average: {
                  readingEquivalentData.length > 0 
                    ? (readingEquivalentData.reduce((sum, d) => sum + d.score, 0) / readingEquivalentData.length).toFixed(1)
                    : 'N/A'
                }</p>
              </div>
              {readingEquivalentData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={[...readingEquivalentData].sort((a, b) => a.score - b.score)} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                    <YAxis label={{ value: 'Score', angle: -90, position: 'insideLeft' }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="score" stroke="#10b981" dot={{ fill: '#10b981' }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-gray-500">No data available</div>
              )}
            </div>

            {/* Age Difference Chart */}
            <div className="space-y-4">
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h3 className="text-lg font-semibold text-orange-900 mb-2">Reading (Age Difference)</h3>
                <p className="text-sm text-orange-700">Average difference: {
                  readingDifferenceData.length > 0 
                    ? (readingDifferenceData.reduce((sum, d) => sum + d.difference, 0) / readingDifferenceData.length).toFixed(2)
                    : 'N/A'
                } years</p>
              </div>
              {readingDifferenceData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={[...readingDifferenceData].sort((a, b) => a.difference - b.difference)} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                    <YAxis 
                      tickFormatter={(value) => Math.round(value)}
                      label={{ value: 'Difference (years)', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip />
                    <Line type="monotone" dataKey="difference" stroke="#f97316" dot={{ fill: '#f97316' }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-gray-500">No data available</div>
              )}
            </div>
          </div>
        </div>
      ) : readingOverallView === 'score' ? (
        <ReadingScoreView />
      ) : (
        <ReadingAgeView />
      )}
    </div>
  );

  const ReadingScoreView = () => {
    const sortedScoreData = [...readingScoreData].sort((a, b) => a.score - b.score);
    
    return (
    <div className="space-y-8">
      <div className="space-y-6">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">Reading (Age Standardised Score)</h3>
          <p className="text-sm text-blue-700">Average score across class: {
            readingScoreData.length > 0 
              ? (readingScoreData.reduce((sum, d) => sum + d.score, 0) / readingScoreData.length).toFixed(1)
              : 'N/A'
          }</p>
        </div>

        {readingScoreData.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={sortedScoreData} margin={{ top: 20, right: 30, left: 20, bottom: 120 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                angle={-45} 
                textAnchor="end" 
                height={100}
              />
              <YAxis label={{ value: 'Score', angle: -90, position: 'insideLeft' }} />
              <Tooltip />
              <Bar dataKey="score" radius={[8, 8, 0, 0]}>
                {sortedScoreData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getScoreColor(entry.score)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-12 text-gray-500">
            No standardised score data available
          </div>
        )}
      </div>

      <div className="space-y-6">
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <h3 className="text-lg font-semibold text-purple-900 mb-2">Score Range Distribution</h3>
          <p className="text-sm text-purple-700">Class distribution by performance level</p>
        </div>

        {readingScoreRangeData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={readingScoreRangeData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
              <YAxis label={{ value: 'Count', angle: -90, position: 'insideLeft' }} />
              <Tooltip />
              <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                {readingScoreRangeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getScoreRangeColor(entry.name)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-12 text-gray-500">
            No score range data available
          </div>
        )}
      </div>
    </div>
    );
  };

  const ReadingAgeView = () => {
    const sortedEquivData = [...readingEquivalentData].sort((a, b) => a.score - b.score);
    const sortedDiffData = [...readingDifferenceData].sort((a, b) => a.difference - b.difference);

    const scoreValues = sortedEquivData.map(d => d.score).filter(v => v !== null);
    const scoreMin = scoreValues.length > 0 ? Math.min(...scoreValues) - 1 : 0;
    const scoreMax = scoreValues.length > 0 ? Math.max(...scoreValues) : 10;

    const diffValues = sortedDiffData.map(d => d.difference).filter(v => v !== null);
    const diffMin = diffValues.length > 0 ? Math.min(...diffValues) - 1 : -5;
    const diffMax = diffValues.length > 0 ? Math.max(...diffValues) : 5;

    return (
    <div className="space-y-8">
      <div className="space-y-4">
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <h3 className="text-lg font-semibold text-green-900 mb-2">Reading (Age Equivalent Score)</h3>
          <p className="text-sm text-green-700">Average: {
            readingEquivalentData.length > 0 
              ? (readingEquivalentData.reduce((sum, d) => sum + d.score, 0) / readingEquivalentData.length).toFixed(1)
              : 'N/A'
          }</p>
        </div>

        {readingEquivalentData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={sortedEquivData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                angle={-45} 
                textAnchor="end" 
                height={80}
              />
              <YAxis 
                domain={[scoreMin, scoreMax]}
                tickFormatter={(value) => Math.round(value)}
                label={{ value: 'Score', angle: -90, position: 'insideLeft' }} 
              />
              <Tooltip />
              <Line type="monotone" dataKey="score" stroke="#10b981" dot={{ fill: '#10b981' }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No equivalent score data available
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <h3 className="text-lg font-semibold text-purple-900 mb-2">Reading (Age Difference)</h3>
          <p className="text-sm text-purple-700">Average difference: {
            readingDifferenceData.length > 0 
              ? (readingDifferenceData.reduce((sum, d) => sum + d.difference, 0) / readingDifferenceData.length).toFixed(2)
              : 'N/A'
          } years</p>
        </div>

        {readingDifferenceData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={sortedDiffData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                angle={-45} 
                textAnchor="end" 
                height={80}
              />
              <YAxis 
                domain={[diffMin, diffMax]}
                tickFormatter={(value) => Math.round(value)}
                label={{ value: 'Difference (years)', angle: -90, position: 'insideLeft' }} 
              />
              <Tooltip />
              <Line type="monotone" dataKey="difference" stroke="#a855f7" dot={{ fill: '#a855f7' }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No age difference data available
          </div>
        )}
      </div>
    </div>
    );
  };

  const ReadingSubjectsView = ({ title, icon, equivData, diffData }) => {
    const sortedEquivData = [...equivData].sort((a, b) => a.score - b.score);
    const sortedDiffData = [...diffData].sort((a, b) => a.difference - b.difference);

    const scoreValues = sortedEquivData.map(d => d.score).filter(v => v !== null);
    const scoreMin = scoreValues.length > 0 ? Math.min(...scoreValues) - 1 : 0;
    const scoreMax = scoreValues.length > 0 ? Math.max(...scoreValues) : 10;

    const diffValues = sortedDiffData.map(d => d.difference).filter(v => v !== null);
    const diffMin = diffValues.length > 0 ? Math.min(...diffValues) - 1 : -5;
    const diffMax = diffValues.length > 0 ? Math.max(...diffValues) : 5;

    return (
      <div className="space-y-8">
        <div className="grid grid-cols-1 gap-8">
          {/* Equivalent Score Chart */}
          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="text-lg font-semibold text-green-900 mb-2">{icon} {title} (Age Equivalent Score)</h3>
              <p className="text-sm text-green-700">Average: {
                equivData.length > 0 
                  ? (equivData.reduce((sum, d) => sum + d.score, 0) / equivData.length).toFixed(1)
                  : 'N/A'
              }</p>
            </div>
            {equivData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={sortedEquivData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                  <YAxis 
                    domain={[scoreMin, scoreMax]}
                    tickFormatter={(value) => Math.round(value)}
                    label={{ value: 'Score', angle: -90, position: 'insideLeft' }} 
                  />
                  <Tooltip />
                  <Line type="monotone" dataKey="score" stroke="#10b981" dot={{ fill: '#10b981' }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-8 text-gray-500">No data available</div>
            )}
          </div>

          {/* Age Difference Chart */}
          <div className="space-y-4">
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <h3 className="text-lg font-semibold text-orange-900 mb-2">{icon} {title} (Age Difference)</h3>
              <p className="text-sm text-orange-700">Average difference: {
                diffData.length > 0 
                  ? (diffData.reduce((sum, d) => sum + d.difference, 0) / diffData.length).toFixed(2)
                  : 'N/A'
              } years</p>
            </div>
            {diffData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={sortedDiffData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                  <YAxis 
                    domain={[diffMin, diffMax]}
                    tickFormatter={(value) => Math.round(value)}
                    label={{ value: 'Difference (years)', angle: -90, position: 'insideLeft' }} 
                  />
                  <Tooltip />
                  <Line type="monotone" dataKey="difference" stroke="#f97316" dot={{ fill: '#f97316' }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-8 text-gray-500">No data available</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Spelling Report Components
  const SpellingOverallView = () => (
    <div className="space-y-6">
      {/* Sub-tabs for Spelling Overall view */}
      <div className="flex gap-2 border-b border-gray-200 mb-6">
        <button
          onClick={() => setSpellingOverallView('view')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-all ${
            spellingOverallView === 'view'
              ? 'bg-blue-100 text-blue-800 border-b-2 border-blue-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          📊 View
        </button>
        <button
          onClick={() => setSpellingOverallView('score')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-all ${
            spellingOverallView === 'score'
              ? 'bg-blue-100 text-blue-800 border-b-2 border-blue-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          📈 Score
        </button>
        <button
          onClick={() => setSpellingOverallView('age')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-all ${
            spellingOverallView === 'age'
              ? 'bg-blue-100 text-blue-800 border-b-2 border-blue-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          🎂 Age
        </button>
      </div>

      {/* Content based on selected view */}
      {spellingOverallView === 'view' ? (
        <div className="space-y-8">
          <div className="grid grid-cols-2 gap-8">
            {/* Score Chart */}
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">Spelling (Age Standardised Score)</h3>
                <p className="text-sm text-blue-700">Average: {
                  spellingScoreData.length > 0 
                    ? (spellingScoreData.reduce((sum, d) => sum + d.score, 0) / spellingScoreData.length).toFixed(1)
                    : 'N/A'
                }</p>
              </div>
              {spellingScoreData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={[...spellingScoreData].sort((a, b) => a.score - b.score)} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                    <YAxis label={{ value: 'Score', angle: -90, position: 'insideLeft' }} />
                    <Tooltip />
                    <Bar dataKey="score" radius={[8, 8, 0, 0]}>
                      {[...spellingScoreData].sort((a, b) => a.score - b.score).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getScoreColor(entry.score)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-gray-500">No score data available</div>
              )}
            </div>

            {/* Score Range Chart */}
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h3 className="text-lg font-semibold text-purple-900 mb-2">Score Range Distribution</h3>
                <p className="text-sm text-purple-700">Class distribution by performance level</p>
              </div>
              {spellingScoreRangeData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={spellingScoreRangeData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                    <YAxis label={{ value: 'Count', angle: -90, position: 'insideLeft' }} />
                    <Tooltip />
                    <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                      {spellingScoreRangeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getScoreRangeColor(entry.name)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-gray-500">No range data available</div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8">
            {/* Equivalent Score Chart */}
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="text-lg font-semibold text-green-900 mb-2">Spelling (Age Equivalent Score)</h3>
                <p className="text-sm text-green-700">Average: {
                  spellingEquivalentData.length > 0 
                    ? (spellingEquivalentData.reduce((sum, d) => sum + d.score, 0) / spellingEquivalentData.length).toFixed(1)
                    : 'N/A'
                }</p>
              </div>
              {spellingEquivalentData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={[...spellingEquivalentData].sort((a, b) => a.score - b.score)} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                    <YAxis label={{ value: 'Score', angle: -90, position: 'insideLeft' }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="score" stroke="#10b981" dot={{ fill: '#10b981' }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-gray-500">No data available</div>
              )}
            </div>

            {/* Age Difference Chart */}
            <div className="space-y-4">
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h3 className="text-lg font-semibold text-orange-900 mb-2">Spelling (Age Difference)</h3>
                <p className="text-sm text-orange-700">Average difference: {
                  spellingDifferenceData.length > 0 
                    ? (spellingDifferenceData.reduce((sum, d) => sum + d.difference, 0) / spellingDifferenceData.length).toFixed(2)
                    : 'N/A'
                } years</p>
              </div>
              {spellingDifferenceData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={[...spellingDifferenceData].sort((a, b) => a.difference - b.difference)} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                    <YAxis 
                      tickFormatter={(value) => Math.round(value)}
                      label={{ value: 'Difference (years)', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip />
                    <Line type="monotone" dataKey="difference" stroke="#f97316" dot={{ fill: '#f97316' }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-gray-500">No data available</div>
              )}
            </div>
          </div>
        </div>
      ) : spellingOverallView === 'score' ? (
        <SpellingScoreView />
      ) : (
        <SpellingAgeView />
      )}
    </div>
  );

  const SpellingScoreView = () => {
    const sortedScoreData = [...spellingScoreData].sort((a, b) => a.score - b.score);
    
    return (
    <div className="space-y-8">
      <div className="space-y-6">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">Spelling (Age Standardised Score)</h3>
          <p className="text-sm text-blue-700">Average score across class: {
            spellingScoreData.length > 0 
              ? (spellingScoreData.reduce((sum, d) => sum + d.score, 0) / spellingScoreData.length).toFixed(1)
              : 'N/A'
          }</p>
        </div>

        {spellingScoreData.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={sortedScoreData} margin={{ top: 20, right: 30, left: 20, bottom: 120 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                angle={-45} 
                textAnchor="end" 
                height={100}
              />
              <YAxis label={{ value: 'Score', angle: -90, position: 'insideLeft' }} />
              <Tooltip />
              <Bar dataKey="score" radius={[8, 8, 0, 0]}>
                {sortedScoreData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getScoreColor(entry.score)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-12 text-gray-500">
            No standardised score data available
          </div>
        )}
      </div>

      <div className="space-y-6">
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <h3 className="text-lg font-semibold text-purple-900 mb-2">Score Range Distribution</h3>
          <p className="text-sm text-purple-700">Class distribution by performance level</p>
        </div>

        {spellingScoreRangeData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={spellingScoreRangeData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
              <YAxis label={{ value: 'Count', angle: -90, position: 'insideLeft' }} />
              <Tooltip />
              <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                {spellingScoreRangeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getScoreRangeColor(entry.name)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-12 text-gray-500">
            No score range data available
          </div>
        )}
      </div>
    </div>
    );
  };

  const SpellingAgeView = () => {
    const sortedEquivData = [...spellingEquivalentData].sort((a, b) => a.score - b.score);
    const sortedDiffData = [...spellingDifferenceData].sort((a, b) => a.difference - b.difference);

    const scoreValues = sortedEquivData.map(d => d.score).filter(v => v !== null);
    const scoreMin = scoreValues.length > 0 ? Math.min(...scoreValues) - 1 : 0;
    const scoreMax = scoreValues.length > 0 ? Math.max(...scoreValues) : 10;

    const diffValues = sortedDiffData.map(d => d.difference).filter(v => v !== null);
    const diffMin = diffValues.length > 0 ? Math.min(...diffValues) - 1 : -5;
    const diffMax = diffValues.length > 0 ? Math.max(...diffValues) : 5;

    return (
    <div className="space-y-8">
      <div className="space-y-4">
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <h3 className="text-lg font-semibold text-green-900 mb-2">Spelling (Age Equivalent Score)</h3>
          <p className="text-sm text-green-700">Average: {
            spellingEquivalentData.length > 0 
              ? (spellingEquivalentData.reduce((sum, d) => sum + d.score, 0) / spellingEquivalentData.length).toFixed(1)
              : 'N/A'
          }</p>
        </div>

        {spellingEquivalentData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={sortedEquivData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                angle={-45} 
                textAnchor="end" 
                height={80}
              />
              <YAxis 
                domain={[scoreMin, scoreMax]}
                tickFormatter={(value) => Math.round(value)}
                label={{ value: 'Score', angle: -90, position: 'insideLeft' }} 
              />
              <Tooltip />
              <Line type="monotone" dataKey="score" stroke="#10b981" dot={{ fill: '#10b981' }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No equivalent score data available
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <h3 className="text-lg font-semibold text-purple-900 mb-2">Spelling (Age Difference)</h3>
          <p className="text-sm text-purple-700">Average difference: {
            spellingDifferenceData.length > 0 
              ? (spellingDifferenceData.reduce((sum, d) => sum + d.difference, 0) / spellingDifferenceData.length).toFixed(2)
              : 'N/A'
          } years</p>
        </div>

        {spellingDifferenceData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={sortedDiffData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
              <YAxis 
                domain={[diffMin, diffMax]}
                tickFormatter={(value) => Math.round(value)}
                label={{ value: 'Difference (years)', angle: -90, position: 'insideLeft' }} 
              />
              <Tooltip />
              <Line type="monotone" dataKey="difference" stroke="#a855f7" dot={{ fill: '#a855f7' }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No age difference data available
          </div>
        )}
      </div>
    </div>
    );
  };

  // Developed Ability Report Components
  const DevelopedAbilityOverallView = () => (
    <div className="space-y-6">
      {/* Sub-tabs for Developed Ability Overall view */}
      <div className="flex gap-2 border-b border-gray-200 mb-6">
        <button
          onClick={() => setDevelopedAbilityOverallView('view')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-all ${
            developedAbilityOverallView === 'view'
              ? 'bg-blue-100 text-blue-800 border-b-2 border-blue-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          📊 View
        </button>
        <button
          onClick={() => setDevelopedAbilityOverallView('score')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-all ${
            developedAbilityOverallView === 'score'
              ? 'bg-blue-100 text-blue-800 border-b-2 border-blue-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          📈 Score
        </button>
        <button
          onClick={() => setDevelopedAbilityOverallView('age')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-all ${
            developedAbilityOverallView === 'age'
              ? 'bg-blue-100 text-blue-800 border-b-2 border-blue-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          🎂 Age
        </button>
      </div>

      {/* Content based on selected view */}
      {developedAbilityOverallView === 'view' ? (
        <div className="space-y-8">
          <div className="grid grid-cols-2 gap-8">
            {/* Score Chart */}
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">Developed Ability (Age Standardised Score)</h3>
                <p className="text-sm text-blue-700">Average: {
                  developedAbilityScoreData.length > 0 
                    ? (developedAbilityScoreData.reduce((sum, d) => sum + d.score, 0) / developedAbilityScoreData.length).toFixed(1)
                    : 'N/A'
                }</p>
              </div>
              {developedAbilityScoreData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={[...developedAbilityScoreData].sort((a, b) => a.score - b.score)} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                    <YAxis label={{ value: 'Score', angle: -90, position: 'insideLeft' }} />
                    <Tooltip />
                    <Bar dataKey="score" radius={[8, 8, 0, 0]}>
                      {[...developedAbilityScoreData].sort((a, b) => a.score - b.score).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getScoreColor(entry.score)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-gray-500">No score data available</div>
              )}
            </div>

            {/* Score Range Chart */}
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h3 className="text-lg font-semibold text-purple-900 mb-2">Score Range Distribution</h3>
                <p className="text-sm text-purple-700">Class distribution by performance level</p>
              </div>
              {developedAbilityScoreRangeData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={developedAbilityScoreRangeData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                    <YAxis label={{ value: 'Count', angle: -90, position: 'insideLeft' }} />
                    <Tooltip />
                    <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                      {developedAbilityScoreRangeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getScoreRangeColor(entry.name)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-gray-500">No range data available</div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8">
            {/* Equivalent Score Chart */}
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="text-lg font-semibold text-green-900 mb-2">Developed Ability (Age Equivalent Score)</h3>
                <p className="text-sm text-green-700">Average: {
                  developedAbilityEquivalentData.length > 0 
                    ? (developedAbilityEquivalentData.reduce((sum, d) => sum + d.score, 0) / developedAbilityEquivalentData.length).toFixed(1)
                    : 'N/A'
                }</p>
              </div>
              {developedAbilityEquivalentData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={[...developedAbilityEquivalentData].sort((a, b) => a.score - b.score)} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                    <YAxis label={{ value: 'Score', angle: -90, position: 'insideLeft' }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="score" stroke="#10b981" dot={{ fill: '#10b981' }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-gray-500">No data available</div>
              )}
            </div>

            {/* Age Difference Chart */}
            <div className="space-y-4">
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h3 className="text-lg font-semibold text-orange-900 mb-2">Developed Ability (Age Difference)</h3>
                <p className="text-sm text-orange-700">Average difference: {
                  developedAbilityDifferenceData.length > 0 
                    ? (developedAbilityDifferenceData.reduce((sum, d) => sum + d.difference, 0) / developedAbilityDifferenceData.length).toFixed(2)
                    : 'N/A'
                } years</p>
              </div>
              {developedAbilityDifferenceData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={[...developedAbilityDifferenceData].sort((a, b) => a.difference - b.difference)} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                    <YAxis 
                      tickFormatter={(value) => Math.round(value)}
                      label={{ value: 'Difference (years)', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip />
                    <Line type="monotone" dataKey="difference" stroke="#f97316" dot={{ fill: '#f97316' }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-gray-500">No data available</div>
              )}
            </div>
          </div>
        </div>
      ) : developedAbilityOverallView === 'score' ? (
        <DevelopedAbilityScoreView />
      ) : (
        <DevelopedAbilityAgeView />
      )}
    </div>
  );

  const DevelopedAbilityScoreView = () => {
    const sortedScoreData = [...developedAbilityScoreData].sort((a, b) => a.score - b.score);
    
    return (
    <div className="space-y-8">
      <div className="space-y-6">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">Developed Ability (Age Standardised Score)</h3>
          <p className="text-sm text-blue-700">Average score across class: {
            developedAbilityScoreData.length > 0 
              ? (developedAbilityScoreData.reduce((sum, d) => sum + d.score, 0) / developedAbilityScoreData.length).toFixed(1)
              : 'N/A'
          }</p>
        </div>

        {developedAbilityScoreData.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={sortedScoreData} margin={{ top: 20, right: 30, left: 20, bottom: 120 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                angle={-45} 
                textAnchor="end" 
                height={100}
              />
              <YAxis label={{ value: 'Score', angle: -90, position: 'insideLeft' }} />
              <Tooltip />
              <Bar dataKey="score" radius={[8, 8, 0, 0]}>
                {sortedScoreData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getScoreColor(entry.score)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-12 text-gray-500">
            No standardised score data available
          </div>
        )}
      </div>

      <div className="space-y-6">
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <h3 className="text-lg font-semibold text-purple-900 mb-2">Score Range Distribution</h3>
          <p className="text-sm text-purple-700">Class distribution by performance level</p>
        </div>

        {developedAbilityScoreRangeData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={developedAbilityScoreRangeData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
              <YAxis label={{ value: 'Count', angle: -90, position: 'insideLeft' }} />
              <Tooltip />
              <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                {developedAbilityScoreRangeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getScoreRangeColor(entry.name)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-12 text-gray-500">
            No score range data available
          </div>
        )}
      </div>
    </div>
    );
  };

  const DevelopedAbilityAgeView = () => {
    const sortedEquivData = [...developedAbilityEquivalentData].sort((a, b) => a.score - b.score);
    const sortedDiffData = [...developedAbilityDifferenceData].sort((a, b) => a.difference - b.difference);

    const scoreValues = sortedEquivData.map(d => d.score).filter(v => v !== null);
    const scoreMin = scoreValues.length > 0 ? Math.min(...scoreValues) - 1 : 0;
    const scoreMax = scoreValues.length > 0 ? Math.max(...scoreValues) : 10;

    const diffValues = sortedDiffData.map(d => d.difference).filter(v => v !== null);
    const diffMin = diffValues.length > 0 ? Math.min(...diffValues) - 1 : -5;
    const diffMax = diffValues.length > 0 ? Math.max(...diffValues) : 5;

    return (
    <div className="space-y-8">
      <div className="space-y-4">
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <h3 className="text-lg font-semibold text-green-900 mb-2">Developed Ability (Age Equivalent Score)</h3>
          <p className="text-sm text-green-700">Average: {
            developedAbilityEquivalentData.length > 0 
              ? (developedAbilityEquivalentData.reduce((sum, d) => sum + d.score, 0) / developedAbilityEquivalentData.length).toFixed(1)
              : 'N/A'
          }</p>
        </div>

        {developedAbilityEquivalentData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={sortedEquivData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                angle={-45} 
                textAnchor="end" 
                height={80}
              />
              <YAxis 
                domain={[scoreMin, scoreMax]}
                tickFormatter={(value) => Math.round(value)}
                label={{ value: 'Score', angle: -90, position: 'insideLeft' }} 
              />
              <Tooltip />
              <Line type="monotone" dataKey="score" stroke="#10b981" dot={{ fill: '#10b981' }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No equivalent score data available
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <h3 className="text-lg font-semibold text-purple-900 mb-2">Developed Ability (Age Difference)</h3>
          <p className="text-sm text-purple-700">Average difference: {
            developedAbilityDifferenceData.length > 0 
              ? (developedAbilityDifferenceData.reduce((sum, d) => sum + d.difference, 0) / developedAbilityDifferenceData.length).toFixed(2)
              : 'N/A'
          } years</p>
        </div>

        {developedAbilityDifferenceData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={sortedDiffData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
              <YAxis 
                domain={[diffMin, diffMax]}
                tickFormatter={(value) => Math.round(value)}
                label={{ value: 'Difference (years)', angle: -90, position: 'insideLeft' }} 
              />
              <Tooltip />
              <Line type="monotone" dataKey="difference" stroke="#a855f7" dot={{ fill: '#a855f7' }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No age difference data available
          </div>
        )}
      </div>
    </div>
    );
  };

  const DevelopedAbilitySubjectsView = ({ title, icon, equivData, diffData }) => {
    const sortedEquivData = [...equivData].sort((a, b) => a.score - b.score);
    const sortedDiffData = [...diffData].sort((a, b) => a.difference - b.difference);

    const scoreValues = sortedEquivData.map(d => d.score).filter(v => v !== null);
    const scoreMin = scoreValues.length > 0 ? Math.min(...scoreValues) - 1 : 0;
    const scoreMax = scoreValues.length > 0 ? Math.max(...scoreValues) : 10;

    const diffValues = sortedDiffData.map(d => d.difference).filter(v => v !== null);
    const diffMin = diffValues.length > 0 ? Math.min(...diffValues) - 1 : -5;
    const diffMax = diffValues.length > 0 ? Math.max(...diffValues) : 5;

    return (
      <div className="space-y-8">
        <div className="grid grid-cols-1 gap-8">
          {/* Equivalent Score Chart */}
          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="text-lg font-semibold text-green-900 mb-2">{icon} {title} (Age Equivalent Score)</h3>
              <p className="text-sm text-green-700">Average: {
                equivData.length > 0 
                  ? (equivData.reduce((sum, d) => sum + d.score, 0) / equivData.length).toFixed(1)
                  : 'N/A'
              }</p>
            </div>
            {equivData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={sortedEquivData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                  <YAxis 
                    domain={[scoreMin, scoreMax]}
                    tickFormatter={(value) => Math.round(value)}
                    label={{ value: 'Score', angle: -90, position: 'insideLeft' }} 
                  />
                  <Tooltip />
                  <Line type="monotone" dataKey="score" stroke="#10b981" dot={{ fill: '#10b981' }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-8 text-gray-500">No data available</div>
            )}
          </div>

          {/* Age Difference Chart */}
          <div className="space-y-4">
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <h3 className="text-lg font-semibold text-orange-900 mb-2">{icon} {title} (Age Difference)</h3>
              <p className="text-sm text-orange-700">Average difference: {
                diffData.length > 0 
                  ? (diffData.reduce((sum, d) => sum + d.difference, 0) / diffData.length).toFixed(2)
                  : 'N/A'
              } years</p>
            </div>
            {diffData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={sortedDiffData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                  <YAxis 
                    domain={[diffMin, diffMax]}
                    tickFormatter={(value) => Math.round(value)}
                    label={{ value: 'Difference (years)', angle: -90, position: 'insideLeft' }} 
                  />
                  <Tooltip />
                  <Line type="monotone" dataKey="difference" stroke="#f97316" dot={{ fill: '#f97316' }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-8 text-gray-500">No data available</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-8 max-w-full">
      {/* Reports Tabs */}
      <div className="flex gap-4 mb-8 border-b border-gray-200">
        <button
          onClick={() => setActiveReport('mathematics')}
          className={`px-4 py-2 font-semibold transition-colors ${
            activeReport === 'mathematics'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          🔢 Mathematics Reports
        </button>
        <button
          onClick={() => setActiveReport('arithmetic')}
          className={`px-4 py-2 font-semibold transition-colors ${
            activeReport === 'arithmetic'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          ➕ Arithmetic Reports
        </button>
        <button
          onClick={() => setActiveReport('reading')}
          className={`px-4 py-2 font-semibold transition-colors ${
            activeReport === 'reading'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          📚 Reading Reports
        </button>
        <button
          onClick={() => setActiveReport('spelling')}
          className={`px-4 py-2 font-semibold transition-colors ${
            activeReport === 'spelling'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          ✏️ Spelling Reports
        </button>
        <button
          onClick={() => setActiveReport('developed-ability')}
          className={`px-4 py-2 font-semibold transition-colors ${
            activeReport === 'developed-ability'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          🧠 Developed Ability Reports
        </button>
      </div>

      {activeReport === 'mathematics' && (
        <div>
          {/* Mathematics Tabs */}
          <div className="mb-6 flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setMathematicsTab('overall')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                mathematicsTab === 'overall'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
            >
              Overall
            </button>
            <button
              onClick={() => setMathematicsTab('numbers-1')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                mathematicsTab === 'numbers-1'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
            >
              1️⃣ Numbers (1)
            </button>
            <button
              onClick={() => setMathematicsTab('numbers-2')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                mathematicsTab === 'numbers-2'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
            >
              2️⃣ Numbers (2)
            </button>
            <button
              onClick={() => setMathematicsTab('measures')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                mathematicsTab === 'measures'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
            >
              📐 Measures, Shape & Space
            </button>
            <button
              onClick={() => setMathematicsTab('data-handling')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                mathematicsTab === 'data-handling'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
            >
              📊 Data Handling
            </button>
          </div>

          {/* Content */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin text-3xl">⏳</div>
              <p className="text-gray-600 mt-2">Loading mathematics data...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              {error}
            </div>
          ) : mathScoreData.length === 0 ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-700">
              No mathematics assessment data available for current students.
            </div>
          ) : mathematicsTab === 'overall' ? (
            <MathematicsOverallView />
          ) : mathematicsTab === 'numbers-1' ? (
            <MathematicsSubjectsView 
              title="Numbers (1)" 
              icon="1️⃣"
              equivData={numbers1EquivData} 
              diffData={numbers1DiffData} 
            />
          ) : mathematicsTab === 'numbers-2' ? (
            <MathematicsSubjectsView 
              title="Numbers (2)" 
              icon="2️⃣"
              equivData={numbers2EquivData} 
              diffData={numbers2DiffData} 
            />
          ) : mathematicsTab === 'measures' ? (
            <MathematicsSubjectsView 
              title="Measures, Shape & Space" 
              icon="📐"
              equivData={measuresEquivData} 
              diffData={measuresDiffData} 
            />
          ) : mathematicsTab === 'data-handling' ? (
            <MathematicsSubjectsView 
              title="Data Handling" 
              icon="📊"
              equivData={dataHandlingEquivData} 
              diffData={dataHandlingDiffData} 
            />
          ) : null}
        </div>
      )}

      {activeReport === 'arithmetic' && (
        <div>
          {/* Arithmetic Tabs */}
          <div className="mb-6 flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setArithmeticTab('overall')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                arithmeticTab === 'overall'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
            >
              Overall
            </button>
            <button
              onClick={() => setArithmeticTab('addition')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                arithmeticTab === 'addition'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
            >
              ➕ Addition
            </button>
            <button
              onClick={() => setArithmeticTab('subtraction')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                arithmeticTab === 'subtraction'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
            >
              ➖ Subtraction
            </button>
            {yearGroup >= 3 && (
              <button
                onClick={() => setArithmeticTab('multiplication')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  arithmeticTab === 'multiplication'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                }`}
              >
                ✖️ Multiplication
              </button>
            )}
            {yearGroup >= 3 && (
              <button
                onClick={() => setArithmeticTab('division')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  arithmeticTab === 'division'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                }`}
              >
                ➗ Division
              </button>
            )}
          </div>

          {/* Content */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin text-3xl">⏳</div>
              <p className="text-gray-600 mt-2">Loading arithmetic data...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              {error}
            </div>
          ) : arithmeticData.length === 0 ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-700">
              No arithmetic assessment data available for current students.
            </div>
          ) : arithmeticTab === 'overall' ? (
            <OverallView />
          ) : arithmeticTab === 'addition' ? (
            <OperationView 
              title="Addition" 
              icon="➕"
              equivData={additionEquivData} 
              diffData={additionDiffData} 
            />
          ) : arithmeticTab === 'subtraction' ? (
            <OperationView 
              title="Subtraction" 
              icon="➖"
              equivData={subtractionEquivData} 
              diffData={subtractionDiffData} 
            />
          ) : arithmeticTab === 'multiplication' ? (
            <OperationView 
              title="Multiplication" 
              icon="✖️"
              equivData={multiplicationEquivData} 
              diffData={multiplicationDiffData} 
            />
          ) : (
            <OperationView 
              title="Division" 
              icon="➗"
              equivData={divisionEquivData} 
              diffData={divisionDiffData} 
            />
          )}
        </div>
      )}

      {activeReport === 'reading' && (
        <div>
          {/* Reading Tabs */}
          <div className="mb-6 flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setReadingTab('overall')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                readingTab === 'overall'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
            >
              Overall
            </button>
            <button
              onClick={() => setReadingTab('word-recognition')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                readingTab === 'word-recognition'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
            >
              📖 Word Recognition
            </button>
            <button
              onClick={() => setReadingTab('comprehension')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                readingTab === 'comprehension'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
            >
              💭 Comprehension
            </button>
          </div>

          {/* Content */}
          {readingData.length === 0 ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-700">
              No reading assessment data available for current students.
            </div>
          ) : readingTab === 'overall' ? (
            <ReadingOverallView />
          ) : readingTab === 'word-recognition' ? (
            <ReadingSubjectsView 
              title="Word Recognition" 
              icon="📖"
              equivData={wordRecognitionEquivData} 
              diffData={wordRecognitionDiffData} 
            />
          ) : (
            <ReadingSubjectsView 
              title="Comprehension" 
              icon="💭"
              equivData={comprehensionEquivData} 
              diffData={comprehensionDiffData} 
            />
          )}
        </div>
      )}

      {activeReport === 'spelling' && (
        <div>
          {/* Spelling Tabs */}
          <div className="mb-6 flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setSpellingTab('overall')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                spellingTab === 'overall'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
            >
              Overall
            </button>
          </div>

          {/* Content */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin text-3xl">⏳</div>
              <p className="text-gray-600 mt-2">Loading spelling data...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              {error}
            </div>
          ) : spellingScoreData.length === 0 ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-700">
              No spelling assessment data available for current students.
            </div>
          ) : spellingTab === 'overall' ? (
            <SpellingOverallView />
          ) : null}
        </div>
      )}

      {activeReport === 'developed-ability' && (
        <div>
          {/* Developed Ability Tabs */}
          <div className="mb-6 flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setDevelopedAbilityTab('overall')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                developedAbilityTab === 'overall'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
            >
              Overall
            </button>
            <button
              onClick={() => setDevelopedAbilityTab('picture-vocabulary')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                developedAbilityTab === 'picture-vocabulary'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
            >
              🖼️ Picture Vocabulary
            </button>
            <button
              onClick={() => setDevelopedAbilityTab('non-verbal')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                developedAbilityTab === 'non-verbal'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
            >
              🔤 Non-Verbal
            </button>
          </div>

          {/* Content */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin text-3xl">⏳</div>
              <p className="text-gray-600 mt-2">Loading developed ability data...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              {error}
            </div>
          ) : developedAbilityScoreData.length === 0 ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-700">
              No developed ability assessment data available for current students.
            </div>
          ) : developedAbilityTab === 'overall' ? (
            <DevelopedAbilityOverallView />
          ) : developedAbilityTab === 'picture-vocabulary' ? (
            <DevelopedAbilitySubjectsView 
              title="Picture Vocabulary" 
              icon="🖼️"
              equivData={pictureVocabEquivData} 
              diffData={pictureVocabDiffData} 
            />
          ) : developedAbilityTab === 'non-verbal' ? (
            <DevelopedAbilitySubjectsView 
              title="Non-Verbal" 
              icon="🔤"
              equivData={nonVerbalEquivData} 
              diffData={nonVerbalDiffData} 
            />
          ) : null}
        </div>
      )}
    </div>
  );
};

export default ReportsTab;
